import { useState, type SetStateAction } from 'react';
import { recordedActionEntries, recordedActionSpans } from './operatorEvidence';
import { useTraceState } from './useTraceState';
import type { Span } from './types';

/**
 * The rows the Actions view opens by itself: the phase still running (finished ones stay folded,
 * so a long loop reads as its plan), every replayed procedure, and the procedure step running now.
 * Together they put the live work on screen without a click per level.
 */
export function defaultOpenActionIds(tree: Span[]): Set<string> {
  const open = new Set<string>();
  function walk(spans: Span[]) {
    for (const span of spans) {
      if (!span.children?.length) continue;
      const running = span.status === 'running';
      const isStep = span.sopStepIndex !== undefined;
      if (
        span.isSopProcedure ||
        (running && (span.isPhase || span.isWorkflowStep || isStep))
      ) {
        open.add(span.id);
      }
      walk(span.children);
    }
  }
  walk(tree);
  return open;
}

/** Both projections share selection; switching views never creates another inspector. */
export function useInspectionState(
  spans: Span[],
  defaultView: 'actions' | 'trace',
  initialRecordId?: string,
  controlledView?: 'actions' | 'trace',
) {
  const [uncontrolledView, setView] = useState(defaultView);
  const view = controlledView ?? uncontrolledView;
  const actionTree = recordedActionSpans(spans);
  const actions = recordedActionEntries(actionTree);
  const [selectionChanged, setSelectionChanged] = useState(false);
  const trace = useTraceState(spans, defaultOpenActionIds(actionTree));
  const initial =
    actions.find(
      (span) => span.operatorEvidence?.record.id === initialRecordId,
    ) ??
    actions[0] ??
    null;
  const selected =
    trace.selected ??
    (defaultView === 'actions' && !selectionChanged ? initial : null);
  const actionsView = view === 'actions' && actions.length > 0;
  function setSelectedId(id: SetStateAction<string | null>) {
    setSelectionChanged(true);
    trace.setSelectedId(id);
  }
  function changeView(next: 'actions' | 'trace') {
    if (!selectionChanged && initial) setSelectedId(initial.id);
    setView(next);
  }
  return {
    actions,
    actionsView,
    selected,
    state: { ...trace, selected, setSelectedId },
    changeView,
    shownSpans: actionsView ? actionTree : spans,
  };
}
